import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { isAdminAuthenticated } from "./auth";
import Navbar from "./Navbar";
import HeroPage from "./HeroPage";
import Footer from "./Footer";
import CategoriesListing from "./CategoriesListing";
import ProductPage from "./ProductPage";
import PrivacyPolicyPage from "./Pages/PrivacyPolicyPage";
import RefundPolicyPage from "./Pages/RefundPolicyPage";
import TermsOfServicePage from "./Pages/TermsOfServicePage";
import ContactPage from "./Pages/ContactPage";
import CampaignsPage from "./Pages/CampaignsPage";
import CampaignShowPage from "./Pages/CampaignShowPage";
import ScrollToTop from "./ScrollToTop";
import ZodiacStonesPage from "./Pages/ZodiacStonesPage";
import NewsletterSubscribePage from "./Pages/NewsletterSubscribePage";
import WhatsAppWidget from "./components/WhatsAppWidget";
import BlogsPage from "./Pages/BlogsPage";
import BlogDetailPage from "./Pages/BlogDetailPage";
import CollectionPage from "./Pages/CollectionPage";
import CollectionsIndexPage from "./Pages/CollectionsIndexPage";
import ProductRedirect from "./ProductRedirect";
import AdminLayout from "./Admin/Layout";
import AdminLoginPage from "./Admin/Pages/AdminLoginPage";
import AdminDashboardPage from "./Admin/Pages/AdminDashboardPage";
import GoogleReviewsAdminPage from "./Admin/Pages/GoogleReviewsAdminPage";
import HomeFaqsAdminPage from "./Admin/Pages/HomeFaqsAdminPage";
import ListingFaqsAdminPage from "./Admin/Pages/ListingFaqsAdminPage";
import ListingIntrosAdminPage from "./Admin/Pages/ListingIntrosAdminPage";
import AdminCollectionsPage from "./Admin/Pages/AdminCollectionsPage";
import AdminBlogsPage from "./Admin/Pages/AdminBlogsPage";
import HeroSectionAdminPage from "./Admin/Pages/HeroSectionAdminPage";
import AnnouncementBannerAdminPage from "./Admin/Pages/AnnouncementBannerAdminPage";
import NewsletterUsersAdminPage from "./Admin/Pages/NewsletterUsersAdminPage";
import NewsletterBannerAdminPage from "./Admin/Pages/NewsletterBannerAdminPage";
import ProductsAdminPage from "./Admin/Pages/ProductsAdminPage";
import CampaignsAdminPage from "./Admin/Pages/CampaignsAdminPage";
import CampaignDetailPage from "./Admin/Pages/CampaignDetailPage";
import ProductDetailPage from "./Admin/Pages/ProductDetailPage";

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  if (!isAdminAuthenticated()) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAdmin>
    <AdminLayout>{children}</AdminLayout>
  </RequireAdmin>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <WhatsAppWidget />
      <Routes>
        {/* PUBLIC ROUTES */}
        <Route
          path="/"
          element={
            <>
              <Navbar />
              <HeroPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/products-listing"
          element={
            <>
              <Navbar />
              <CategoriesListing />
              <Footer />
            </>
          }
        />
        <Route
          path="/product/:slug"
          element={
            <>
              <Navbar />
              <ProductPage />
              <Footer />
            </>
          }
        />
        <Route path="/product/:id" element={<ProductRedirect />} />
        <Route
          path="/privacy-policy"
          element={
            <>
              <Navbar />
              <PrivacyPolicyPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/refund-policy"
          element={
            <>
              <Navbar />
              <RefundPolicyPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/terms-of-service"
          element={
            <>
              <Navbar />
              <TermsOfServicePage />
              <Footer />
            </>
          }
        />
        <Route
          path="/contact"
          element={
            <>
              <Navbar />
              <ContactPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/campaigns"
          element={
            <>
              <Navbar />
              <CampaignsPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/campaigns/:id"
          element={
            <>
              <Navbar />
              <CampaignShowPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/zodiac-stones"
          element={
            <>
              <Navbar />
              <ZodiacStonesPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/newsletter"
          element={
            <>
              <Navbar />
              <NewsletterSubscribePage />
              <Footer />
            </>
          }
        />
        <Route
          path="/blogs"
          element={
            <>
              <Navbar />
              <BlogsPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/blogs/:slug"
          element={
            <>
              <Navbar />
              <BlogDetailPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/collections"
          element={
            <>
              <Navbar />
              <CollectionsIndexPage />
              <Footer />
            </>
          }
        />
        <Route
          path="/collections/:slug"
          element={
            <>
              <Navbar />
              <CollectionPage />
              <Footer />
            </>
          }
        />

        {/* ADMIN LOGIN - no layout needed */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* ADMIN ROUTES - all wrapped with AdminRoute */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboardPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/products"
          element={
            <AdminRoute>
              <ProductsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/products/new"
          element={
            <AdminRoute>
              <ProductDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/products/:productId"
          element={
            <AdminRoute>
              <ProductDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/campaigns"
          element={
            <AdminRoute>
              <CampaignsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/campaigns/:id"
          element={
            <AdminRoute>
              <CampaignDetailPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/blogs"
          element={
            <AdminRoute>
              <AdminBlogsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/collections"
          element={
            <AdminRoute>
              <AdminCollectionsPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/hero"
          element={
            <AdminRoute>
              <HeroSectionAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/announcement-banner"
          element={
            <AdminRoute>
              <AnnouncementBannerAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/newsletter-users"
          element={
            <AdminRoute>
              <NewsletterUsersAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/newsletter-banner"
          element={
            <AdminRoute>
              <NewsletterBannerAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/google-reviews"
          element={
            <AdminRoute>
              <GoogleReviewsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/faqs/home"
          element={
            <AdminRoute>
              <HomeFaqsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/faqs/listing"
          element={
            <AdminRoute>
              <ListingFaqsAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/listing-intros"
          element={
            <AdminRoute>
              <ListingIntrosAdminPage />
            </AdminRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
